<template>
  <div v-show="isPanelVisible" class="controller-container" :class="{
    'is-closing': isClosing,
    'is-centered': store.debugPanel.isCentered
  }" :style="panelStyle" @click.stop @contextmenu.prevent>
    <Transition name="view-swap" mode="out-in">

      <div v-if="!activeSection" class="control-bar" key="main-bar">
        <div class="main-section">
          <label class="section-label">参数调整</label>
          <div class="section-group">
            <button v-for="(section, index) in sections" :key="section.key" class="bar-button" :style="{ '--i': index }"
              @click="activeSection = section.key">
              {{ section.title }}
            </button>
          </div>
        </div>

    <div class="preset-section">
          <label class="section-label">预设模式</label>
          <div class="preset-group">
            <button
              v-for="preset in presets"
              :key="preset.key"
              class="preset-button"
              :title="`预设: ${preset.title}`"
              @click="applyPreset(preset.key)"
            >
              <div class="preset-icon" :class="`icon--${preset.key}`"></div>
              <span class="preset-text">{{ preset.title }}</span>
            </button>
          </div>
        </div>
        
        <div class="preset-section">
          <label class="section-label">导出为PNG</label>
          <div class="export-group">
            <button 
              v-for="res in resolutions" 
              :key="res.key"
              class="export-button"
              :class="{ active: selectedResolution === res.key }"
              @click="selectedResolution = res.key"
            >
              <span class="export-text">{{ res.label }}</span>
            </button>
            <button 
              class="export-action-button"
              title="导出当前画面"
              @click="exportSceneAsPNG()"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </button>
          </div>
        </div>

      </div>

      <div v-else class="detail-panel" key="detail-panel">
        <div class="header">
          <button class="back-button" @click="activeSection = null">← {{ currentSectionTitle }}</button>
        </div>
        <div class="separator"></div>
        <div class="content">
          <div v-if="activeSection === 'camera'">
            <div class="button-group">
              <button :class="{ active: store.sceneParams.cameraType === 'Orthographic' }"
                @click="store.sceneParams.cameraType = 'Orthographic'">
                2D 正交
              </button>
              <button :class="{ active: store.sceneParams.cameraType === 'Perspective' }"
                @click="store.sceneParams.cameraType = 'Perspective'">
                3D 透视
              </button>
            </div>
            <Transition name="slide-fade">
              <div v-if="store.sceneParams.cameraType === 'Perspective'" class="slider-group indented">
                <label>视野 (FOV): {{ store.sceneParams.perspectiveFov }}</label>
                <input type="range" min="10" max="120" step="1" v-model.number="store.sceneParams.perspectiveFov" />
                <label>相机远近: {{ store.sceneParams.perspectiveZ }}</label>
                <input type="range" min="10" max="200" step="1" v-model.number="store.sceneParams.perspectiveZ" />
              </div>
            </Transition>
          </div>

          <div v-if="activeSection === 'geometry'" class="slider-group">
            <label>横向数量: {{ store.sceneParams.PLANE_COUNT_X }}</label>
            <input type="range" min="10" max="100" step="1" v-model.number="store.sceneParams.PLANE_COUNT_X" />
            <label>纵向数量: {{ store.sceneParams.PLANE_COUNT_Y }}</label>
            <input type="range" min="5" max="50" step="1" v-model.number="store.sceneParams.PLANE_COUNT_Y" />
            <label>滚动速度: {{ store.sceneParams.SCROLL_SPEED.toFixed(2) }}</label>
            <input type="range" min="0" max="2" step="0.05" v-model.number="store.sceneParams.SCROLL_SPEED" />
            <label>深度间距: {{ store.sceneParams.planeDepthSpacing.toFixed(2) }}</label>
            <input type="range" min="0" max="2" step="0.05" v-model.number="store.sceneParams.planeDepthSpacing" />
            <label>视觉高度: {{ store.sceneParams.planeVisualHeight.toFixed(1) }}</label>
            <input type="range" min="1" max="20" step="0.5" v-model.number="store.sceneParams.planeVisualHeight" />
          </div>

          <div v-if="activeSection === 'effects'" class="slider-group">
            <label>山脉起伏: {{ store.sceneParams.BULGE_AMOUNT.toFixed(1) }}</label>
            <input type="range" min="0" max="50" step="0.5" v-model.number="store.sceneParams.BULGE_AMOUNT" />
            <label>波动频率: {{ store.sceneParams.IDLE_ANIM_FREQUENCY.toFixed(2) }}</label>
            <input type="range" min="0" max="5" step="0.1" v-model.number="store.sceneParams.IDLE_ANIM_FREQUENCY" />
            <label>波动幅度: {{ store.sceneParams.IDLE_ANIM_AMPLITUDE.toFixed(1) }}</label>
            <input type="range" min="0" max="20" step="0.5" v-model.number="store.sceneParams.IDLE_ANIM_AMPLITUDE" />
          </div>

          <div v-if="activeSection === 'vertex'" class="slider-group">
            <label>倾斜: {{ store.sceneParams.shader_vertex_slant.toFixed(4) }}</label>
            <input type="range" min="0" max="0.02" step="0.0001"
              v-model.number="store.sceneParams.shader_vertex_slant" />
            <label>挤压: {{ store.sceneParams.shader_vertex_squash.toFixed(2) }}</label>
            <input type="range" min="0" max="2" step="0.05" v-model.number="store.sceneParams.shader_vertex_squash" />
            <label>缩放: {{ store.sceneParams.shader_vertex_size.toFixed(2) }}</label>
            <input type="range" min="0" max="2" step="0.05" v-model.number="store.sceneParams.shader_vertex_size" />
          </div>

          <div v-if="activeSection === 'fragment'" class="slider-group">
            <label>拖尾: {{ store.sceneParams.shader_trail_factor.toFixed(1) }}</label>
            <input type="range" min="1" max="10" step="0.1" v-model.number="store.sceneParams.shader_trail_factor" />
            <label>顶部颜色: {{ store.sceneParams.shader_top_color_multiplier.toFixed(1) }}</label>
            <input type="range" min="0" max="10" step="0.1"
              v-model.number="store.sceneParams.shader_top_color_multiplier" />
            <label>底部颜色: {{ store.sceneParams.shader_bottom_color_multiplier.toFixed(1) }}</label>
            <input type="range" min="0" max="5" step="0.1"
              v-model.number="store.sceneParams.shader_bottom_color_multiplier" />
            <label>全局透明度: {{ store.sceneParams.shader_global_alpha.toFixed(2) }}</label>
            <input type="range" min="0" max="1" step="0.01" v-model.number="store.sceneParams.shader_global_alpha" />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useMainStore } from '@/stores/mainStore';
import * as THREE from 'three'; // 【新增】导入 THREE

const store = useMainStore();



// --- 内部动画与状态控制 ---
const isPanelVisible = ref(false);
const isClosing = ref(false);


// --- 从 Store 订阅状态 ---
const storeVisibility = computed(() => store.debugPanel.isVisible);
const panelStyle = computed(() => {
  return store.debugPanel.isCentered ? {} : store.debugPanel.position;
});

// --- 监听 Store 变化来控制显隐和动画 ---
watch(storeVisibility, (newValue) => {
  if (newValue) {
    isClosing.value = false;
    isPanelVisible.value = true;
    activeSection.value = null;
  } else {
    if (isPanelVisible.value) {
      closePanelWithAnimation();
    }
  }
});

const closePanelWithAnimation = () => {
  isClosing.value = true;
  setTimeout(() => {
    isPanelVisible.value = false;
    isClosing.value = false;
  }, 300);
};


const activeSection = ref(null);

// --- 导出功能逻辑 ---
const resolutions = ref([
  { key: 'screen', label: '屏幕分辨率', width: window.innerWidth, height: window.innerHeight },
  { key: '1080p', label: '1080p', width: 1920, height: 1080 },
  { key: '2k', label: '2K', width: 2560, height: 1440 },
  { key: '4k', label: '4K', width: 3840, height: 2160 },
  { key: '8k', label: '8K', width: 7680, height: 4320 },
]);
// 【修改】将初始值设置为键名 'screen'
const selectedResolution = ref(resolutions.value[0].key);

const getResolutionData = (key) => {
  return resolutions.value.find(res => res.key === key);
};

// 【修改】移除 res 参数
const exportSceneAsPNG = async () => {
  const { scene, renderer, camera } = store;
  if (!scene || !renderer || !camera) {
    console.error('Scene, renderer, or camera not registered.');
    return;
  }
  
  // 【新增】根据选定的 key 获取分辨率数据
  const res = getResolutionData(selectedResolution.value);

  const offscreenRenderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true
  });
  offscreenRenderer.setSize(res.width, res.height);

  const originalBackground = scene.background;
  scene.background = null;

  offscreenRenderer.render(scene, camera);

  try {
    const dataURL = offscreenRenderer.domElement.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = `稻城色韵-${res.label}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Failed to export:', error);
  } finally {
    scene.background = originalBackground;
    offscreenRenderer.dispose();
  }
};

const sections = ref([
  { key: 'camera', title: '视角' },
  { key: 'geometry', title: '布局' },
  { key: 'effects', title: '波形' },
  { key: 'vertex', title: '顶点' },
  { key: 'fragment', title: '着色' }
]);

const currentSectionTitle = computed(() => {
  return sections.value.find(s => s.key === activeSection.value)?.title || '';
});

// --- Presets concepts updated ---
const presets = ref([
  { key: 'default', title: '原境' },
  { key: 'calm', title: '画卷' },
  { key: 'dynamic', title: '幡涌' },
  { key: 'soft', title: '薄雾' }
]);

const applyPreset = (presetKey) => {
  store.applyScenePreset(presetKey);
  console.log(`Applying preset: ${presetKey}`);
};


// --- 事件监听 ---
const handleContextMenu = (event) => {
  store.showDebugPanel({
    centered: false,
    position: { top: `${event.clientY}px`, left: `${event.clientX}px` }
  });
};

const handleClickOutside = () => {
  if (store.debugPanel.isVisible) {
    store.hideDebugPanel();
  }
};

const handleEscKey = (e) => {
  if (e.key === 'Escape' && store.debugPanel.isVisible) {
    store.hideDebugPanel();
  }
};

onMounted(() => {
  window.addEventListener('contextmenu', handleContextMenu);
  window.addEventListener('click', handleClickOutside);
  window.addEventListener('keydown', handleEscKey);
});

onUnmounted(() => {
  window.removeEventListener('contextmenu', handleContextMenu);
  window.removeEventListener('click', handleClickOutside);
  window.removeEventListener('keydown', handleEscKey);
});
</script>

<style scoped>
/* --- Base Container --- */
.controller-container {
  position: fixed;
  z-index: 2000;
  color: #000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  transform: translate(20px, 0px);
  animation: panel-fade-in 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.controller-container.is-centered {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.controller-container.is-closing {
  animation: panel-fade-out 0.3s cubic-bezier(0.5, 0, 0.75, 0) forwards;
}

@keyframes panel-fade-in {
  from {
    opacity: 0;
    transform: translate(var(--translateX, 20px), var(--translateY, 0px)) scale(0.9);
  }

  to {
    opacity: 1;
    transform: translate(var(--translateX, 20px), var(--translateY, 0px)) scale(1);
  }
}

.controller-container {
  --translateX: 20px;
  --translateY: 0px;
}

.controller-container.is-centered {
  --translateX: -50%;
  --translateY: -50%;
}

@keyframes panel-fade-out {
  from {
    opacity: 1;
    transform: translate(var(--translateX, 20px), var(--translateY, 0px)) scale(1);
  }

  to {
    opacity: 0;
    transform: translate(var(--translateX, 20px), var(--translateY, 0px)) scale(0.9);
  }
}

/* --- Main Control Bar Layout --- */
.control-bar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  background: rgba(245, 245, 247, 0.8);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  animation: bar-fade-in 0.3s cubic-bezier(0.25, 1, 0.5, 1);
}

@keyframes bar-fade-in {
  from {
    opacity: 0;
    transform: scale(0.9);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* --- Section & Preset Group Styles --- */
.main-section,
.preset-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.preset-section {
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.section-label {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  padding: 0 4px;
  font-weight: 500;
  user-select: none;
}

.section-group {
  display: flex;
  gap: 4px;
  padding: 4px;
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
}

.preset-group {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

/* --- Detail Panel --- */
.detail-panel {
  width: 280px;
  padding: 12px;
  background: rgba(245, 245, 247, 0.8);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  height: 24px;
}

.separator {
  width: 100%;
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
  margin-bottom: 12px;
}

.content {
  padding-top: 4px;
}

/* --- Shared Button Styles --- */
button {
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
  letter-spacing: 0.5px;
  text-align: center;
  user-select: none;
}

button:hover {
  color: #000;
}

button:active {
  transform: scale(0.95);
}

/* Parameter Section Buttons */
.bar-button {
  flex-grow: 1;
  padding: 6px 10px;
  border-radius: 5px;
  opacity: 0;
  transform: translateY(10px);
  animation: button-slide-in 0.4s cubic-bezier(0.25, 1, 0.5, 1) forwards;
  animation-delay: calc(var(--i) * 0.05s);
  white-space: nowrap;
}

.bar-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* --- [MODIFIED] Preset Buttons Final Style --- */
.preset-button {
  display: flex;
  flex-direction: column;
  /* Icon on top, text below */
  align-items: center;
  justify-content: center;
  gap: 4px;
  /* Space between icon and text */
  height: 50px;
  /* Increased height */
  padding: 6px 4px;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.04);
  border: 1px solid transparent;
}

.preset-button:hover {
  background-color: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.05);
}

.preset-button:active {
  transform: scale(0.96);
  background-color: rgba(0, 0, 0, 0.1);
}

.preset-text {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1;
  transition: color 0.2s ease;
}

.preset-button:hover .preset-text {
  color: rgba(0, 0, 0, 0.9);
}


/* --- CSS Icons for Preset Buttons --- */
.preset-icon {
  width: 18px;
  height: 16px;
  /* Adjusted height for icons */
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.preset-button:hover .preset-icon {
  opacity: 0.9;
}

/* 1. 原境 (default) - 均衡 */
.icon--default {
  background:
    radial-gradient(circle, currentColor 2px, transparent 2px),
    radial-gradient(circle, currentColor 2px, transparent 2px);
  background-size: 8px 8px;
  background-position: 1px 2px, 5px 6px;
}

/* 2. 画卷 (calm) - 平远 */
.icon--calm {
  background: repeating-linear-gradient(currentColor 0,
      currentColor 2px,
      transparent 2px,
      transparent 5px);
}

/* 3. 溪流 (dynamic) - 流动 */
.icon--dynamic {
  background: repeating-linear-gradient(-45deg,
      currentColor 0,
      currentColor 2px,
      transparent 2px,
      transparent 6px);
}

/* 4. 薄雾 (soft) - 朦胧 */
.icon--soft {
  background: radial-gradient(circle, currentColor 50%, transparent 70%);
  opacity: 0.4;
}

.preset-button:hover .icon--soft {
  opacity: 0.6;
}

@keyframes button-slide-in {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header .back-button {
  width: 100%;
  text-align: left;
  padding: 4px;
}

.button-group {
  display: flex;
  justify-content: space-around;
  margin-bottom: 14px;
}

.button-group button {
  padding: 5px 0;
  position: relative;
}

.button-group button.active {
  color: #000;
}

.button-group button.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 50%;
  height: 1px;
  background-color: #000;
}

.slider-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.indented {
  margin-top: 12px;
  padding-left: 10px;
}

label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
  transition: color 0.2s ease;
}

.slider-group:has(input[type="range"]:active) label {
  color: #000;
}

input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  background: transparent;
  outline: none;
  margin-top: -2px;
}

input[type="range"]::-webkit-slider-runnable-track {
  height: 2px;
  background: rgba(0, 0, 0, 0.2);
}

input[type="range"]::-moz-range-track {
  height: 2px;
  background: rgba(0, 0, 0, 0.2);
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  background: #000;
  border-radius: 50%;
  cursor: grab;
  margin-top: -6px;
  transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1);
}

input[type="range"]::-moz-range-thumb {
  width: 14px;
  height: 14px;
  background: #000;
  border-radius: 50%;
  cursor: grab;
  border: none;
}

input[type="range"]:active::-webkit-slider-thumb {
  transform: scale(1.3);
  cursor: grabbing;
}

input[type="range"]:active::-moz-range-thumb {
  transform: scale(1.3);
  cursor: grabbing;
}

.view-swap-enter-active,
.view-swap-leave-active {
  transition: all 0.25s cubic-bezier(0.25, 1, 0.5, 1);
}

.view-swap-enter-from {
  opacity: 0;
  transform: scale(0.9);
}

.view-swap-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-8px);
  opacity: 0;
}

/* --- 新增导出功能样式 --- */
.export-group {
  display: flex;
  gap: 4px;
  padding: 4px;
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
}

.export-button {
  flex-grow: 1;
  padding: 6px 4px;
  border-radius: 5px;
  opacity: 0.8;
  transition: all 0.2s ease;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
}
.export-button:hover {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.7);
}
.export-button.active {
  background-color: rgba(0, 0, 0, 0.1);
  color: #000;
  opacity: 1;
}

.export-action-button {
  width: 32px;
  height: 32px;
  min-width: 32px;
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 5px;
  transition: all 0.2s ease;
  padding: 6px;
}
.export-action-button:hover {
  background-color: rgba(0, 0, 0, 0.08);
}
.export-action-button svg {
  width: 100%;
  height: 100%;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}
.export-action-button:hover svg {
  opacity: 0.9;
}
</style>